// ─── A container's route, and its progress along it ──────────────────────────
//
// The client-side twin of `lineSteps` / `completedStepCount` / `deriveLineStatus`
// in models/ContainerOrderLine.js, and it exists for the same reason the
// start-requirement list does: the server is the gate, but the forms have to
// know what the gate will say before they offer a control that leads to it.
//
// Everything here is derived from the stamps on the container's own stops. The
// stored `status` is not consulted — it is itself derived from these, and where
// the two disagree (a row from before the step statuses existed, a cancellation
// laid over real progress) the stamps are what actually happened.
//
// None of it is a constant any more. The run used to be a fixed six steps, so a
// positional table said it all; a route that can gain a weighbridge or lose its
// drop-off terminal cannot be indexed that way, and two containers on the same
// order can legitimately have different runs. So the steps are asked of the
// container.

import {
  ANCHOR_ROLES,
  STEP_FIELDS,
  STEP_NAMES_BY_ROLE,
} from "../types/containerOrders";
import type {
  ContainerLineStatus,
  ContainerLineStepPlan,
  ContainerOrderLine,
  ContainerRouteStop,
  ContainerStopRole,
  LineStep,
  StepField,
} from "../types/containerOrders";

/**
 * The status a container holds once a stop's step is recorded, by role.
 *
 * Mirrors `STATUS_BY_ROLE` on the server. The words are unchanged for the
 * three-stop route, which is what the vast majority of moves are, so nothing
 * displayed shifts under existing containers. `returned` only ever appears when
 * something is scheduled *after* the drop-off — on an ordinary route the
 * drop-off is the last step and finishing it means `completed`.
 */
const STATUS_BY_ROLE: Record<
  ContainerStopRole,
  readonly [ContainerLineStatus, ContainerLineStatus]
> = {
  terminalPickup: ["at_pickup_terminal", "picked_up"],
  loadingSite: ["at_loading_site", "loaded"],
  terminalDropoff: ["at_dropoff_terminal", "returned"],
  custom: ["at_stop", "stop_completed"],
};

/**
 * Enough of a container to answer questions about its route.
 *
 * `status` and `plannedAt` are on it for one question the route cannot answer:
 * whether dispatch has released the container. Both are optional, and an object
 * carrying neither reads as released — see `isLinePlanned`.
 */
type RouteSource = Partial<
  Pick<
    ContainerOrderLine,
    | "routeStops"
    | "terminalPickup"
    | "loadingSite"
    | "terminalDropoff"
    | "plannedAt"
    | "status"
  >
>;

/**
 * Whether dispatch has released this container to the truck.
 *
 * Mirrors `isLinePlanned` on the server, including its fallback: a container
 * written before `plannedAt` existed carries no stamp and was released by
 * definition — `planned` was the status every container was created with. Only
 * one that actually says `created` is waiting on the office.
 */
export function isLinePlanned(line: RouteSource | null | undefined): boolean {
  if (line?.plannedAt) return true;
  return line?.status !== "created";
}

/**
 * This container's stops, in route order.
 *
 * The API always sends `routeStops` — it states the three anchors on the way
 * out for any container written before routes could vary — so the fallback here
 * is for the one caller the API is not: a form holding a draft it has not saved
 * yet, and any stored object that reached the client by another path.
 */
export function routeStopsOf(
  line: RouteSource | null | undefined,
): ContainerRouteStop[] {
  if (line?.routeStops?.length) return line.routeStops;

  return ANCHOR_ROLES.map((role, position) => ({
    ...(line?.[role] ?? {}),
    // The role stands in for an id on a container that has no stored route.
    // It is unique across a route by construction, which is all a key needs.
    _id: role,
    role,
    position,
    enabled: true,
  })) as ContainerRouteStop[];
}

/** The stops the container is actually going to make, in order. */
export const activeStopsOf = (line: RouteSource | null | undefined) =>
  routeStopsOf(line).filter((stop) => stop.enabled !== false);

/** The stops the dispatcher has marked as not being made, in order. */
export const skippedStopsOf = (line: RouteSource | null | undefined) =>
  routeStopsOf(line).filter((stop) => stop.enabled === false);

/**
 * The ordered steps of *this* container's run.
 *
 * Two per stop the container is actually going to make. A stop turned off
 * contributes nothing — no step to record, no row on the timeline, no
 * confirmation offered to the driver — which is the whole difference between
 * "we are not doing this" and "we have not done this yet".
 */
export function lineSteps(
  line: RouteSource | null | undefined,
): ContainerLineStepPlan[] {
  const steps: ContainerLineStepPlan[] = [];

  routeStopsOf(line).forEach((stop, stopIndex) => {
    if (stop.enabled === false) return;
    const names = STEP_NAMES_BY_ROLE[stop.role] ?? STEP_NAMES_BY_ROLE.custom;

    STEP_FIELDS.forEach((field, half) => {
      steps.push({
        step: names[half] as LineStep,
        field: field as StepField,
        stopIndex,
        stopId: String(stop._id),
        role: stop.role,
      });
    });
  });

  return steps;
}

/**
 * How many leading steps are stamped.
 *
 * The completed *prefix*, not the total count: the run is sequential, so a gap
 * means the container is only as far as the gap. A stamp beyond one is a record
 * of something that happened, but not of a container that got there in the
 * order it had to.
 */
export function completedStepCount(
  line: RouteSource | null | undefined,
): number {
  const stops = routeStopsOf(line);
  let count = 0;

  for (const { stopIndex, field } of lineSteps(line)) {
    if (!stops[stopIndex]?.[field]) break;
    count += 1;
  }

  return count;
}

/**
 * The status a container has *earned*, off its stamps.
 *
 * "Every step done" is what `completed` means — not "the drop-off terminal was
 * cleared". On the ordinary route those are the same sentence; on a route whose
 * drop-off is not being used, or which has a stop after it, only the first is.
 */
export function derivedLineStatus(
  line: RouteSource | null | undefined,
): ContainerLineStatus {
  const steps = lineSteps(line);
  const done = completedStepCount(line);

  // Nothing driven yet, so the log has nothing to say and the only question
  // left is whether the office has released the box.
  if (!steps.length || done === 0) {
    return isLinePlanned(line) ? "planned" : "created";
  }
  if (done >= steps.length) return "completed";

  const last = steps[done - 1];
  const byRole = STATUS_BY_ROLE[last.role] ?? STATUS_BY_ROLE.custom;
  return byRole[STEP_FIELDS.indexOf(last.field)];
}

/**
 * The status recording the step at `index` would produce, or null when there is
 * no such step.
 */
export function statusAfterStep(
  line: RouteSource | null | undefined,
  index: number,
): ContainerLineStatus | null {
  const steps = lineSteps(line);
  if (index < 0 || index >= steps.length) return null;
  if (index === steps.length - 1) return "completed";

  const { role, field } = steps[index];
  const byRole = STATUS_BY_ROLE[role] ?? STATUS_BY_ROLE.custom;
  return byRole[STEP_FIELDS.indexOf(field)];
}

/**
 * The one status the office may move this container to next, or null when
 * there is none.
 *
 * Null for a cancelled container — it is not running, and restoring it is a
 * separate decision — and for one that has finished its route. Anchored to the
 * log rather than to the stored status, so the option offered is the one the
 * server will accept.
 */
export function nextLineStatus(
  line: (RouteSource & Pick<ContainerOrderLine, "status">) | null | undefined,
): ContainerLineStatus | null {
  if (!line || line.status === "cancelled") return null;
  // An unreleased container advances to `planned` and to nothing else: it is on
  // no driver's screen, so nobody could have driven it anywhere, and the server
  // refuses every step status against it.
  if (!isLinePlanned(line)) return "planned";
  return statusAfterStep(line, completedStepCount(line));
}

/** The step the container is waiting on, or null once the route is finished. */
export function nextLineStep(
  line: (RouteSource & Pick<ContainerOrderLine, "status">) | null | undefined,
): ContainerLineStepPlan | null {
  if (!line || line.status === "cancelled") return null;
  // Nothing is owed on a container nobody has sent out.
  if (!isLinePlanned(line)) return null;
  return lineSteps(line)[completedStepCount(line)] ?? null;
}
